import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/films";

const navItems = [
  { to: "/", label: "首页" },
  { to: "/category", label: "分类" },
  { to: "/search", label: "搜索" },
  { to: "/about", label: "关于" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 w-full transition-colors duration-300 ${
          scrolled ? "bg-background-50/95 backdrop-blur-md" : "bg-background-50/40 backdrop-blur-sm"
        }`}
      >
        <div className="h-px w-full bg-accent-500/60" />
        <div className="flex h-16 w-full items-center justify-between gap-4 px-4 md:h-[72px] md:px-8 lg:px-12">
          <Link to="/" className="group flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-500 text-background-50">
              <i className="ri-film-line text-lg" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg font-bold tracking-wide text-foreground-950">三级片</span>
              <span className="mt-1 font-label text-[10px] uppercase tracking-[0.28em] text-foreground-500">
                Cinema Archive
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `relative rounded-md px-4 py-2 font-label text-sm transition-colors ${
                    isActive
                      ? "text-accent-500"
                      : "text-foreground-700 hover:text-foreground-950"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <form onSubmit={submitSearch} className="hidden lg:block">
              <div className="flex h-10 w-[280px] items-center gap-2 rounded-full border border-background-300 bg-background-100/80 pl-4 pr-1 transition-colors focus-within:border-accent-500">
                <i className="ri-search-line text-sm text-foreground-500" />
                <input
                  type="text"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索片名、导演、关键词"
                  aria-label="站内搜索"
                  className="h-full min-w-0 flex-1 bg-transparent font-label text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
                />
                <button
                  type="submit"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-500 text-background-50 transition-colors hover:bg-accent-600"
                  aria-label="搜索"
                >
                  <i className="ri-arrow-right-line text-sm" />
                </button>
              </div>
            </form>

            <span className="hidden items-center gap-1 rounded-full border border-accent-500/40 px-2.5 py-1 font-label text-[11px] text-accent-500 xl:flex">
              <i className="ri-shield-check-line" />
              18+
            </span>

            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-800 md:hidden"
              aria-label="打开菜单"
            >
              <i className="ri-menu-3-line text-lg" />
            </button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div
            className="absolute inset-0 bg-background-50/80 backdrop-blur-sm"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute right-0 top-0 flex h-full w-[82%] max-w-[340px] flex-col bg-background-100">
            <div className="flex h-16 items-center justify-between border-b border-background-300 px-4">
              <span className="font-heading text-base font-bold text-foreground-950">导航</span>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-800"
                aria-label="关闭菜单"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <form onSubmit={submitSearch} className="px-4 pt-5">
              <div className="flex h-11 items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4">
                <i className="ri-search-line text-sm text-foreground-500" />
                <input
                  type="text"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="输入关键词"
                  aria-label="站内搜索"
                  className="h-full min-w-0 flex-1 bg-transparent font-label text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
                />
                <button type="submit" className="font-label text-sm text-accent-500" aria-label="提交搜索">
                  搜索
                </button>
              </div>
            </form>

            <nav className="mt-4 flex flex-col px-2">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `flex items-center justify-between rounded-md px-3 py-3.5 font-label text-sm ${
                      isActive ? "bg-background-200 text-accent-500" : "text-foreground-800"
                    }`
                  }
                >
                  <span>{item.label}</span>
                  <i className="ri-arrow-right-s-line text-base text-foreground-500" />
                </NavLink>
              ))}
            </nav>

            <div className="mt-2 px-4">
              <p className="mb-3 font-label text-[11px] uppercase tracking-[0.2em] text-foreground-500">分类</p>
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <Link
                    key={category.key}
                    to={`/category?type=${category.key}`}
                    className="rounded-full border border-background-300 px-3 py-1.5 font-label text-xs text-foreground-700"
                  >
                    {category.name}
                  </Link>
                ))}
              </div>
            </div>

            <p className="mt-auto px-4 pb-6 font-label text-[11px] leading-relaxed text-foreground-500">
              本站为影视资料演示站点，内容均为虚构示例，仅面向成年用户。
            </p>
          </div>
        </div>
      )}
    </>
  );
}